import React, { useState, useEffect } from 'react';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { Button } from 'primereact/button';
import { fetchAppDownloadDetails, fetchApplications } from '../../services/api';
import { AppData, ModuleData } from '../../types/appTypes';

type MobileViewProps = {
    data: ModuleData[];
};

const formatDate = (ms: number | string | Date) => {
    const date = new Date(ms);
    return date.toLocaleDateString();
};

const MobileCardView: React.FC<MobileViewProps> = ({ data }) => {
    const [appsByModule, setAppsByModule] = useState<Record<number, AppData[]>>({});
    const [loadingModule, setLoadingModule] = useState<number | null>(null);
    const [activeIndex, setActiveIndex] = useState<number | null>(data.length > 0 ? 0 : null);

    useEffect(() => {
        if (data.length > 0) {
            handleTabOpen(0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    const handleTabOpen = async (index: number | null) => {
        setActiveIndex(index);
        if (index === null) return;
        const module = data[index];
        if (!module) return;
        if (appsByModule[Number(module.appId)]) return; // already loaded

        setLoadingModule(Number(module.appId));
        try {
            const result = await fetchApplications(Number(module.appId));
            const rawApps = Array.isArray(result) ? result : result.data;
            const apps: AppData[] = rawApps.map((app: any, idx: number) => ({
                id: app.id ?? String(app.appId ?? idx),
                appName: app.appName,
                os: app.os,
                releaseType: app.releaseType,
                releaseDescription: app.releaseDescription,
                releaseDate: app.releaseDate,
                releaseVersion: app.releaseVersion,
                downloadUrl: app.downloadUrl,
            }));
            setAppsByModule(prev => ({ ...prev, [Number(module.appId)]: apps }));
        } finally {
            setLoadingModule(null);
        }
    };

    async function handleDownload(app: AppData): Promise<void> {
        try {
            const response = await fetchAppDownloadDetails(app.id, app.releaseVersion + '.apk');
            const url = response;
            if (url) {
                const appName =
                    data.find(m => String(m.appId) === String(app.id))?.appName ?? app.appName;
                const fileName = `${appName}_${app.releaseVersion}.apk`;
                const link = document.createElement('a');
                link.href = url;
                link.download = fileName;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }
        } catch (error) {
            console.error("Download failed", error);
        }
    }

    return (
        <Accordion
            activeIndex={activeIndex !== null ? activeIndex : undefined}
            onTabChange={(e) => {
                let index: number | null;
                if (typeof e.index === 'number') {
                    index = (activeIndex === e.index) ? null : e.index;
                } else if (Array.isArray(e.index)) {
                    index = e.index.length > 0 ? e.index[0] : null;
                } else {
                    index = null;
                }
                handleTabOpen(index);
            }}
        >
            {data.map((module, idx) => (
                <AccordionTab header={module.appName} key={module.appId}>
                    {loadingModule === Number(module.appId) && <div>Loading...</div>}
                    {appsByModule[Number(module.appId)] && appsByModule[Number(module.appId)].length > 0 ? (
                        appsByModule[Number(module.appId)].map((app, i) => (
                            <div key={i} style={{
                                display: 'flex', flexDirection: 'row', alignItems: 'start',
                                border: '1px solid #000', padding: 5, borderRadius: 8, backgroundColor: '#F9F9F9', marginBottom: 8
                            }}>
                                {/* Left: Icon */}
                                <div style={{
                                    width: '35%', display: 'flex', flexDirection: 'column',
                                    alignItems: 'center', marginRight: 16, marginTop: 20
                                }}>
                                    {(() => {
                                        const imageDetail = data.find((m: ModuleData) => String(m.appId) === String(app.id))?.imageDetail;
                                        if (imageDetail) {
                                            return (
                                                <img
                                                    src={`data:image/png;base64,${imageDetail}`}
                                                    alt="App Icon"
                                                    style={{ width: 48, height: 48, objectFit: 'contain' }}
                                                />
                                            );
                                        }
                                        return (
                                            <i className="pi pi-android" style={{ fontSize: 48, color: '#2D6DF6' }} />
                                        );
                                    })()}
                                </div>
                                {/* Right: Form Fields */}
                                <div style={{ flex: 1, width: '65%' }}>
                                    <div className='mb-3 w-full' style={{ flex: 1 }}>
                                        <h2>{module.appName}</h2>
                                    </div>
                                    <div className="app-details">
                                        <div className="app-detail-row">
                                            <label className="app-detail-label">
                                                <b>Version : </b>{app.releaseVersion}
                                            </label>
                                        </div>
                                        <div className="app-detail-row">
                                            <label className="app-detail-label">
                                                <b>Released Date: </b>{app.releaseDate !== undefined ? formatDate(app.releaseDate) : 'N/A'}
                                            </label>
                                        </div>
                                        <div className="app-detail-row">
                                            <label className="app-detail-label">
                                                <b>OS :</b> Android
                                            </label>
                                        </div>
                                        <div className="app-detail-row">
                                            <label className="app-detail-label">
                                                <b>Release Description :</b> {app.releaseDescription}
                                            </label>
                                        </div>
                                    </div>
                                    <div className='flex justify-end'>
                                        <Button
                                            type="button"
                                            label="Download"
                                            style={{
                                                backgroundColor: '#2D6DF6',
                                                color: 'white',
                                                fontSize: '0.85rem',
                                                padding: '0.25rem 0.75rem',
                                                height: '2rem'
                                            }} onClick={() => handleDownload(app)}
                                        />
                                    </div>
                                </div>
                            </div>
                        ))
                    ) : loadingModule !== Number(module.appId) && <div>No releases found.</div>}
                </AccordionTab>
            ))}
        </Accordion>
    );
};

export default MobileCardView;
